import webApiClient from './client';

/**
 * Instructor API client for managing instructor profiles, locations, and settings
 * @endpoint /api/v2/instructor/*
 * @authentication Required (JWT)
 */
export const instructorApi = {
  /**
   * Get instructor profile with standardSchedule, locations, and settings
   * @param {string} organisationId - Organisation ID
   * @returns {Promise<Object>} profile - { instructorProfile, standardSchedule, preferredLocations[] }
   * @endpoint GET /api/v2/instructor/manage/profile/:organisationId
   */
  getProfile: async (organisationId) => {
    const response = await webApiClient.get(
      `/api/v2/instructor/manage/profile/${organisationId}`
    );

    if (!response.ok || !response.data || !response.data.profile) {
      // V2 errors are RFC 7807 shaped: { error: { code, message, retryable } }. Reading a
      // top-level `message` yields undefined and silently collapses every failure into the
      // generic fallback, losing the code callers need to tell "no profile yet" from "broken".
      const apiError = response.data?.error;
      const err = new Error(apiError?.message || response.data?.message || 'Failed to load instructor profile');
      err.code = apiError?.code;
      err.status = response.status;
      throw err;
    }

    // V2 API wraps response in { success, message, profile }
    // Trust contract guarantee - profile field always present on success
    return response.data.profile;
  },

  /**
   * Update instructor profile (bio, specialties, hourlyRate, etc)
   * @param {string} organisationId - Organisation ID
   * @param {Object} profileData - { bio?, specialties?, hourlyRate?, phoneNumber?, lunchBreak? }
   * @returns {Promise<Object>} updatedProfile
   * @endpoint PUT /api/v2/instructor/manage/profile/:organisationId
   */
  updateProfile: async (organisationId, profileData) => {
    const response = await webApiClient.put(
      `/api/v2/instructor/manage/profile/${organisationId}`,
      profileData
    );

    if (!response.ok || !response.data || !response.data.profile) {
      throw new Error(response.data?.message || 'Failed to update instructor profile');
    }

    return response.data.profile;
  },

  /**
   * Get share links for the instructor's public profile
   * @returns {Promise<Object>} shareData - { webUrl, deepLink, shareText, qrCodeData }
   * @endpoint GET /api/v2/instructor/manage/profile/share-url
   */
  getShareUrl: async () => {
    const response = await webApiClient.get('/api/v2/instructor/manage/profile/share-url');

    if (!response.ok || !response.data || !response.data.shareData) {
      throw new Error(response.data?.message || 'Failed to fetch share URL');
    }

    return response.data.shareData;
  },

  /**
   * Add new teaching location with address and pricing
   * @param {string} organisationId - Organisation ID
   * @param {Object} locationData - { description, address, latitude, longitude, locationDescription?, venueHire? }
   * @returns {Promise<Object>} newLocation - Includes generated locationId
   * @endpoint POST /api/v2/instructor/manage/locations/:organisationId
   */
  addLocation: async (organisationId, locationData) => {
    const response = await webApiClient.post(
      `/api/v2/instructor/manage/locations/${organisationId}`,
      locationData
    );

    if (!response.ok || !response.data || !response.data.location) {
      throw new Error(response.data?.message || 'Failed to add location');
    }

    return response.data.location;
  },

  /**
   * Update existing location details or pricing
   * @param {string} organisationId - Organisation ID
   * @param {string} locationId - Location ID from preferredLocations[]
   * @param {Object} locationData - Partial location fields to update
   * @returns {Promise<Object>} updatedLocation
   * @endpoint PUT /api/v2/instructor/manage/locations/:organisationId/:locationId
   */
  updateLocation: async (organisationId, locationId, locationData) => {
    const response = await webApiClient.put(
      `/api/v2/instructor/manage/locations/${organisationId}/${locationId}`,
      locationData
    );

    if (!response.ok || !response.data || !response.data.location) {
      throw new Error(response.data?.message || 'Failed to update location');
    }

    return response.data.location;
  },

  /**
   * Toggle location active/inactive status (soft delete)
   * @param {string} organisationId - Organisation ID
   * @param {string} locationId - Location ID
   * @param {boolean} isActive - true to activate, false to deactivate
   * @returns {Promise<Object>} location - Updated location with new isActive status
   * @endpoint PATCH /api/v2/instructor/manage/locations/:organisationId/:locationId/status
   */
  toggleLocationStatus: async (organisationId, locationId, isActive) => {
    const response = await webApiClient.patch(
      `/api/v2/instructor/manage/locations/${organisationId}/${locationId}/status`,
      { isActive }
    );

    if (!response.ok || !response.data || !response.data.location) {
      throw new Error(response.data?.message || 'Failed to toggle location status');
    }

    return response.data.location;
  },

  /**
   * Set location as default (updates all locations, setting one as isDefault=true)
   * @param {string} organisationId - Organisation ID
   * @param {string} locationId - Location ID to set as default
   * @returns {Promise<Object>} location - Updated location with isDefault=true
   * @endpoint PATCH /api/v2/instructor/manage/locations/:organisationId/:locationId/set-default
   */
  setDefaultLocation: async (organisationId, locationId) => {
    const response = await webApiClient.patch(
      `/api/v2/instructor/manage/locations/${organisationId}/${locationId}/set-default`
    );

    if (!response.ok || !response.data || !response.data.location) {
      throw new Error(response.data?.message || 'Failed to set default location');
    }

    return response.data.location;
  },

  /**
   * Update instructor profile visibility flags — searchable (directory listing)
   * and/or accepting-bookings (booking CTA). The two are independent.
   * @param {string} organisationId - Organisation ID
   * @param {object} flags - any of { isSearchable, isAcceptingBookings } (booleans); send whichever you're changing.
   * @returns {Promise<void>}
   * @endpoint PATCH /api/v2/instructor/manage/profile/:organisationId/visibility
   */
  toggleVisibility: async (organisationId, flags) => {
    const response = await webApiClient.patch(
      `/api/v2/instructor/manage/profile/${organisationId}/visibility`,
      flags
    );

    if (!response.ok) {
      throw new Error(response.data?.message || 'Failed to update profile visibility');
    }
  },

  /**
   * Permanently delete teaching location (hard delete - cannot undo)
   * @param {string} organisationId - Organisation ID
   * @param {string} locationId - Location ID to delete
   * @returns {Promise<Object>} deletedLocation - Confirmation object
   * @endpoint DELETE /api/v2/instructor/manage/locations/:organisationId/:locationId
   * @warning Permanent deletion - use toggleLocationStatus for soft delete
   */
  deleteLocation: async (organisationId, locationId) => {
    const response = await webApiClient.delete(
      `/api/v2/instructor/manage/locations/${organisationId}/${locationId}`
    );

    if (!response.ok || !response.data || !response.data.location) {
      throw new Error(response.data?.message || 'Failed to delete location');
    }

    return response.data.location;
  },

  /**
   * Get public instructor profile for student discovery
   * @param {string} instructorId - Instructor organisation ID
   * @returns {Promise<object>} Instructor profile object
   * @throws {Error} If fetch fails
   * @endpoint GET /api/v2/instructor/public/:instructorId
   */
  getPublicProfile: async instructorId => {
    const response = await webApiClient.get(`/api/v2/instructor/public/${instructorId}`);

    if (!response.ok || !response.data || !response.data.instructor) {
      throw new Error(response.data?.message || 'Failed to fetch instructor profile');
    }

    return response.data.instructor;
  },

  /**
   * Submit a public enquiry to an instructor
   * Returns raw response — callers check .ok manually for silent error handling.
   * @param {string} instructorId - Instructor organisation ID
   * @param {object} data - Enquiry data (name, email, message, etc.)
   * @returns {Promise<object>} Raw response
   * @endpoint POST /api/v2/instructor/public/enquiry
   */
  submitPublicEnquiry: async (instructorId, data) => {
    return webApiClient.post('/api/v2/instructor/public/enquiry', { instructorId, ...data });
  },

  /**
   * Upload instructor cover image (separate from school org cover)
   * @param {string|null} base64Image - Pure base64 string (no data: prefix), or null to clear
   * @returns {Promise<object>} Updated instructor profile
   * @endpoint PATCH /api/v2/instructor/manage/profile/cover
   */
  updateInstructorCover: async base64Image => {
    const response = await webApiClient.patch("/api/v2/instructor/manage/profile/cover", { base64Image });
    if (!response.ok) {
      throw new Error(response.data?.message || "Failed to upload cover image");
    }
    return response.data.profile;
  },
};
